/**
 * useCamera
 *
 * 端末のカメラを起動して MediaStream を返すフック。
 *
 * 動きのイメージ：
 *   1. マウントされたら背面カメラ優先で getUserMedia を呼ぶ
 *   2. 成功したら state.stream に保持
 *   3. 失敗したら理由を日本語メッセージにして state.error に入れる
 *   4. アンマウント時にトラックを止めてリソースを解放（LEDが点きっぱなしを防ぐ）
 *
 * 注意：
 *   - getUserMedia は HTTPS（または localhost）でないと動かない
 *   - iOS Safari では facingMode: 'environment' を「ideal」で指定するのが安全
 *     （exact だと該当カメラが無い時にエラーになる）
 */

import { useEffect, useRef, useState } from 'react';

/** カメラ起動状態 */
export type CameraStatus = 'idle' | 'requesting' | 'ready' | 'error';

/** useCamera が返す値 */
export interface UseCameraResult {
  stream: MediaStream | null;
  status: CameraStatus;
  /** 失敗時のユーザー向け日本語メッセージ */
  error: string | null;
}

/**
 * getUserMedia の DOMException を、ユーザーに見せる日本語メッセージへ変換
 */
function toUserMessage(err: unknown): string {
  // DOMException 以外（型違反等）はそのまま汎用メッセージ
  if (!(err instanceof DOMException)) {
    return 'カメラの起動中に予期しないエラーが発生しました。';
  }

  switch (err.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      // ユーザーが「許可しない」を押した、またはHTTPSでないなど
      return 'カメラの使用が許可されていません。ブラウザの設定からカメラを許可してください。';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return '使用できるカメラが見つかりませんでした。';
    case 'NotReadableError':
      // 他アプリがカメラを掴んでいる等
      return 'カメラを開けませんでした。他のアプリがカメラを使用していないか確認してください。';
    default:
      return `カメラの起動に失敗しました（${err.name}）。`;
  }
}

export function useCamera(): UseCameraResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // StrictMode の二重実行や再レンダー時にも、最新のストリームを ref で追える
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    // ブラウザがそもそも対応していないケース（IE等）
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('error');
      setError('お使いのブラウザはカメラ機能に対応していません。');
      return;
    }

    let cancelled = false;
    setStatus('requesting');
    setError(null);

    navigator.mediaDevices
      .getUserMedia({
        video: {
          // ideal にしておくと、背面カメラが無い端末でも前面カメラで起動する
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      .then((mediaStream) => {
        // useEffect のクリーンアップが先に走っていたら、ここで取得したストリームは捨てる
        if (cancelled) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = mediaStream;
        setStream(mediaStream);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[useCamera] getUserMedia failed:', err);
        setError(toUserMessage(err));
        setStatus('error');
      });

    // アンマウント時：必ずカメラを解放する
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  return { stream, status, error };
}
